"use client";

import { useEffect, useRef } from "react";
import { useAnalyticsContext, TrackingEvent, ProductType } from "@/analytics";
import { buildHotelEcommerceItem } from "@/analytics/services/hotel/utils";
import { getHotelListContext } from "@/analytics/services/hotel/hotelListContextStorage";

/**
 * Otel detay sayfası için view_item analytics hook.
 * Sayfa yüklendiğinde (product hazır olduğunda) bir kez tetiklenir.
 * Item verisi buildHotelEcommerceItem ile üretilir; datalayer alanları view_item_list / select_item ile aynı
 * (item_category2–5, hotel_country, hotel_region, state, hotel_theme, start_date, end_date, affiliation vb.).
 *
 * dataLayer sırası:
 * 1. dataLayer.push({ ecommerce: null });
 * 2. dataLayer.push({ event: "ecom_event", event_category: "ecommerce", event_action: "view_item", ecommerce: {...} });
 *
 * @param {Object} params
 * @param {Object} params.product - Detay otel verisi (buildHotelEcommerceItem uyumlu: Address, Tags, MarketType, ProviderId, CheckinDate, CheckoutDate, price, Currency, SurveyScore, StarNumber, isRefundable, vb.)
 * @param {Object} params.passengers - { guest_total, guest_adult_number, guest_children_number }
 * @param {string} [params.itemListId]
 * @param {string} [params.itemListName]
 * @param {string} [params.crossProductPromotion]
 * @param {string|number} [params.basketId] - Response'tan gelen BasketId; varsa ecommerce.basket_id olarak gönderilir
 */
export function useHotelDetailViewItemAnalytics({
  product,
  passengers,
  itemListId = "hotel_detail",
  itemListName = "Hotel Detail",
  crossProductPromotion,
  basketId,
}) {
  const { dispatch: trackingDispatch } = useAnalyticsContext();
  const lastProductIdRef = useRef(null);

  useEffect(() => {
    if (!product) return;

    // Select_item'da kaydedilen liste bağlamı (search / landing); yoksa fallback
    const stored = getHotelListContext();
    const effectiveListId = stored?.item_list_id ?? itemListId;
    const effectiveListName = stored?.item_list_name ?? itemListName;

    const productId =
      product?.TurnaHotelId ?? product?.hotelId ?? product?.HotelId ?? product?.id ?? "";
    // Aynı otel için tekrar tetiklenmesin; farklı otele geçildiyse tetikle
    if (lastProductIdRef.current === productId) return;
    lastProductIdRef.current = productId;

    const items = buildHotelEcommerceItem({
      product,
      index: 1,
      item_list_id: effectiveListId,
      item_list_name: effectiveListName,
      cross_product_promotion: crossProductPromotion,
    });

    const firstItem = items?.[0];
    const currency =
      firstItem?.currency || product?.Currency || product?.PriceCurrency || "TRY";
    // value: netPrice istenir. Detay sayfası product'ında `price` net olacak şekilde set ediliyor.
    const value = typeof product?.price === "number" ? product.price : undefined;

    const ecommerce = {
      item_list_id: effectiveListId,
      item_list_name: effectiveListName,
      currency,
      ...(typeof value === "number" ? { value } : {}),
      passengers,
      items,
      ...(basketId != null && String(basketId).trim() !== ""
        ? { basket_id: String(basketId) }
        : {}),
    };

    trackingDispatch({
      type: TrackingEvent.VIEW_ITEM,
      payload: {
        productType: ProductType.HOTEL,
        ecommerce,
      },
    });
  }, [
    product,
    passengers,
    itemListId,
    itemListName,
    crossProductPromotion,
    basketId,
    trackingDispatch,
  ]);

  return {};
}
